import React, { useMemo, useState } from 'react';
import {
    Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle,
    InputAdornment, TextField, Typography,
} from '@mui/material';
import { Search as SearchIcon } from '@mui/icons-material';
import {
    SecurityIdentityFieldMenu,
    securityIdentityFieldLabel,
} from '../commonComponents/SecurityIdentityFieldSelector';
import {
    SECURITY_IDENTITY_FIELD_OPTIONS,
    securityCountry,
    securityIdentityPresentation,
} from '../../utils/securityIdentityPresentation';
import { isValidPortfolioQuantity } from '../../utils/portfolioEditorDraft';
import { CountryFlagText } from '../shared/securityDisplayPrimitives';
import DataTable from '../data_table/DataTable';
import { sortByText } from '../data_table/sorting';
import TextInput from '../ui/TextInput';
import Tooltip from '../ui/Tooltip';

const INITIAL_SORTING = Object.freeze([{ id: 'identity_summary', desc: false }]);

// Both this dialog and the Watchlist portfolio column edit the same draft.
// Closing the dialog retains it; only Discard or a successful save clears it.
export default function PortfolioPositionsDialog({
    rows,
    stateForRow,
    onMembershipChange,
    onQuantityChange,
    onSave,
    onDiscard,
    onClose,
    dirty,
    disabled,
    saving,
    stale,
    error,
    blockedMessage,
}) {
    const [search, setSearch] = useState('');
    const [identityDisplayField, setIdentityDisplayField] = useState('company_name');
    const options = useMemo(() => rows.map((row) => ({
        ...row,
        id: row.portfolio_selection_token || row.id,
    })), [rows]);
    const canEdit = (row) => Boolean(
        !disabled && !saving && !stale
        && row.portfolio_selection_token && row.portfolio_editability?.editable,
    );
    // Checked rows, including those the search hides (the table keeps ids
    // outside its rows as they are).
    const rowSelection = Object.fromEntries(
        options.filter((row) => stateForRow(row).checked).map((row) => [row.id, true]),
    );
    const handleRowSelectionChange = (nextSelection) => {
        options.forEach((row) => {
            const checked = Boolean(nextSelection[row.id]);
            if (canEdit(row) && stateForRow(row).checked !== checked) {
                onMembershipChange(row, checked);
            }
        });
    };
    const invalid = options.some((row) => {
        const state = stateForRow(row);
        return state.checked && !isValidPortfolioQuantity(state.quantity);
    });
    const filteredOptions = useMemo(() => {
        const query = search.trim().toLowerCase();
        return options.filter((row) => !query || [
            ...SECURITY_IDENTITY_FIELD_OPTIONS.map(({ value }) => (
                securityIdentityPresentation(row, value).value
            )),
            securityCountry(row),
        ].some((value) => value.toLowerCase().includes(query)));
    }, [options, search]);
    // Rebuilt every render: the cells read the shared draft (stateForRow),
    // and new column defs make the table re-render its rows.
    const columns = [
        {
            id: 'identity_summary',
            header: securityIdentityFieldLabel(identityDisplayField),
            minSize: 240,
            accessorFn: (row) => securityIdentityPresentation(row, identityDisplayField).value,
            sortFn: sortByText,
            cell: ({ row: { original } }) => {
                const presentation = securityIdentityPresentation(original, identityDisplayField);
                return (
                    <Tooltip
                        content={original.portfolio_editability?.message || presentation.tooltip || presentation.value}
                    >
                        <span>{presentation.value}</span>
                    </Tooltip>
                );
            },
            meta: {
                grow: true,
                headerControls: () => (
                    <SecurityIdentityFieldMenu value={identityDisplayField} onChange={setIdentityDisplayField} />
                ),
            },
        },
        {
            id: 'country',
            header: 'Country',
            size: 130,
            accessorFn: (row) => securityCountry(row),
            sortFn: sortByText,
            cell: ({ getValue }) => <CountryFlagText country={getValue()} />,
        },
        {
            id: 'quantity',
            header: 'Shares',
            size: 180,
            enableSorting: false,
            cell: ({ row: { original: row } }) => {
                const state = stateForRow(row);
                const label = securityIdentityPresentation(row, 'ticker').value;
                // The existing staging contract preserves held quantities.
                if (row.in_actual_portfolio) {
                    return (
                        <Tooltip content={state.draft && state.checked ? 'Pending holding shares' : 'Current holding shares'}>
                            <span>{state.checked ? state.quantity : row.actual_quantity}</span>
                        </Tooltip>
                    );
                }
                const invalidQuantity = state.checked && !isValidPortfolioQuantity(state.quantity);
                return (
                    <TextInput
                        value={state.checked ? state.quantity : ''}
                        disabled={!canEdit(row) || !state.checked}
                        error={invalidQuantity}
                        placeholder="Enter shares"
                        inputMode="decimal"
                        aria-label={`Shares for ${label}`}
                        aria-describedby={invalidQuantity ? 'portfolio-shares-help' : undefined}
                        onChange={(event) => onQuantityChange(row, event.target.value)}
                        // Keys typed here stay here (Escape does not close the dialog).
                        onKeyDown={(event) => event.stopPropagation()}
                    />
                );
            },
            meta: { wrap: true },
        },
    ];

    return (
        <Dialog open onClose={saving ? undefined : onClose} maxWidth="md" fullWidth aria-labelledby="portfolio-positions-title">
            <DialogTitle id="portfolio-positions-title">Add Watchlist Securities</DialogTitle>
            <DialogContent dividers>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Select securities and enter shares for your actual portfolio. Current holdings
                    are checked; uncheck a holding to remove it. Changes are shared with the
                    Watchlist Portfolio column and take effect after review and apply.
                </Typography>
                <Typography id="portfolio-shares-help" variant="caption" color={invalid ? 'error' : 'text.secondary'} sx={{ display: 'block', mb: 1.5 }}>
                    Enter a finite, nonzero number of shares. Fractional shares and negative shares
                    for short positions are supported.
                </Typography>
                {stale && <Alert severity="warning" sx={{ mb: 1.5 }}>The portfolio or Watchlist changed. Discard changes and review the current rows before saving.</Alert>}
                {blockedMessage && <Alert severity="info" sx={{ mb: 1.5 }}>{blockedMessage}</Alert>}
                {error && <Alert severity="error" sx={{ mb: 1.5 }}>{error}</Alert>}
                <TextField
                    fullWidth size="small" label="Search watchlist securities"
                    value={search} onChange={(event) => setSearch(event.target.value)}
                    placeholder="Search company, ticker, submitted identifier, or country"
                    slotProps={{ input: { startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> } }}
                    sx={{ mb: 1.5 }}
                />
                <DataTable
                    aria-label="Watchlist securities"
                    data={filteredOptions}
                    columns={columns}
                    initialSorting={INITIAL_SORTING}
                    rowSelection={rowSelection}
                    onRowSelectionChange={handleRowSelectionChange}
                    enableRowSelection={canEdit}
                    height={420}
                    rowHeight={46}
                    virtualize
                />
            </DialogContent>
            <DialogActions>
                <Button onClick={onDiscard} disabled={!dirty || saving}>Discard changes</Button>
                <Box sx={{ flex: 1 }} />
                <Button onClick={onClose} disabled={saving}>Close</Button>
                <Button variant="contained" onClick={onSave} disabled={!dirty || disabled || saving || stale || invalid}>
                    {saving ? 'Preparing review…' : 'Review changes'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
